/***************************************************
 * src/App.js
 ***************************************************/
import React, { useState, useEffect, createContext, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useNavigate,
  useLocation,
} from "react-router-dom";
import ReactGA from "react-ga4";
import CookieConsent from "react-cookie-consent";
import { GoogleOAuthProvider } from "@react-oauth/google";

import Home from "./Home";
import LoginForm from "./LoginForm";
import RegistrationForm from "./RegistrationForm";
import ThemeToggle from "./ThemeToggle"; // <-- Use the external file version
import Survey from "./Survey";
import TakeSurvey from "./TakeSurvey";
import ThankYou from "./ThankYou";
import AnalysisPage from "./AnalysisPage";
import Footer from "./Footer";
import About from "./About";
import HowTo from "./HowTo";
import ResultsPage from "./ResultsPage";
import Pricing from "./Pricing";
import TryIt from "./TryIt";
import Privacy from "./Privacy";
import TermsOfService from "./TermsOfService";

export const AuthContext = createContext();

// Example scroll-to-top and analytics
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname });
  }, [location]);
  return null;
}

function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [token, setToken] = useState(null);
  const [cookieConsent, setCookieConsent] = useState(
    localStorage.getItem("cookieConsent") === "true"
  );

  useEffect(() => {
    // Load theme
    const savedMode = localStorage.getItem("darkMode") === "true";
    setIsDarkMode(savedMode);
    if (savedMode) document.body.classList.add("dark");

    // Load auth token
    const savedToken = localStorage.getItem("authToken");
    if (savedToken) setToken(savedToken);
  }, []);

  useEffect(() => {
    if (cookieConsent) {
      ReactGA.initialize("G-SVPW3EE1KN"); // Replace with your GA ID
    }
  }, [cookieConsent]);

  useEffect(() => {
    const faviconLink = document.getElementById("fav-icon");
    if (faviconLink) {
      faviconLink.href = isDarkMode ? "/faviconDark.png" : "/faviconLight.png";
    }
  }, [isDarkMode]);

  const handleToggle = () => {
    setIsDarkMode((prev) => {
      const newMode = !prev;
      if (newMode) document.body.classList.add("dark");
      else document.body.classList.remove("dark");
      localStorage.setItem("darkMode", newMode);
      return newMode;
    });
  };

  const login = (userToken) => {
    setToken(userToken);
    localStorage.setItem("authToken", userToken);
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem("authToken");
    alert("You have been logged out!");
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <AuthContext.Provider value={{ token, login, logout }}>
        <Router>
          <ScrollToTop />
          {cookieConsent && <AnalyticsTracker />}
          <div className="App">
            <Header
              token={token}
              logout={logout}
              isDarkMode={isDarkMode}
              onToggle={handleToggle}
            />

            <div className="page-body">
              <div className="main-content">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/howto" element={<HowTo />} />
                  <Route path="/pricing" element={<Pricing />} />
                  
                  {/* PASS isDarkMode HERE */}
                  <Route path="/login" element={<LoginForm isDarkMode={isDarkMode} />} />

                  <Route
                    path="/register"
                    element={<RegistrationForm isDarkMode={isDarkMode} />}
                  />
                  <Route path="/survey" element={<Survey />} />
                  <Route
                    path="/survey/:slug"
                    element={<TakeSurvey isDarkMode={isDarkMode} />}
                  />
                  <Route path="/thank-you" element={<ThankYou />} />
                  <Route path="/analysis/:id" element={<AnalysisPage />} />
                  <Route path="/results" element={<ResultsPage />} />
                  <Route path="/try-it" element={<TryIt isDarkMode={isDarkMode} />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  {/* /auth-success if needed */}
                </Routes>
              </div>
            </div>

            <Footer />

            <CookieConsent
              location="bottom"
              buttonText="I Accept"
              declineButtonText="Decline"
              enableDeclineButton
              cookieName="cookieConsent"
              style={{ background: "#2B373B", color: "#fff" }}
              buttonStyle={{ color: "#4e503b", fontSize: "14px" }}
              declineButtonStyle={{
                color: "#fff",
                fontSize: "14px",
                background: "#555",
              }}
              onAccept={() => {
                localStorage.setItem("cookieConsent", "true");
                setCookieConsent(true);
              }}
              onDecline={() => {
                localStorage.setItem("cookieConsent", "false");
                setCookieConsent(false);
              }}
            >
              This website uses cookies to enhance the user experience.{" "}
              <Link to="/privacy" style={{ color: "#fff", textDecoration: "underline" }}>
                Learn more
              </Link>
            </CookieConsent>
          </div>
        </Router>
      </AuthContext.Provider>
    </GoogleOAuthProvider>
  );
}

export default App;

/************************************************
 * Header Component
 ************************************************/
function Header({ token, logout, isDarkMode, onToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const menuToggle = document.getElementById("menu-toggle");
      if (menuToggle && menuToggle.checked) {
        if (menuRef.current && !menuRef.current.contains(e.target)) {
          menuToggle.checked = false;
        }
      }
    };
    const handleScroll = () => {
      const menuToggle = document.getElementById("menu-toggle");
      if (menuToggle && menuToggle.checked) {
        menuToggle.checked = false;
      }
    };
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleLogoutClick = () => {
    logout();
    navigate("/");
  };

  const showPricingTab = [
    "/",
    "/about",
    "/results",
    "/register",
    "/login",
    "/pricing",
    "/try-it",
  ].includes(pathname);

  const isSurveyCreationPage = pathname === "/survey";
  const isTakeSurveyPage =
    pathname.startsWith("/survey/") && pathname.length > "/survey/".length;
  const isAnalysisPage = pathname.startsWith("/analysis/");

  let navItems = null;

  if (isTakeSurveyPage) {
    navItems = (
      <>
        <Link to="/">Home</Link>
        <Link to="/about">About</Link>
        {showPricingTab && <Link to="/pricing">Pricing</Link>}
        <ThemeToggle isDarkMode={isDarkMode} onToggle={onToggle} />
      </>
    );
  } else if (isSurveyCreationPage && token) {
    navItems = (
      <>
        <Link to="/about">About</Link>
        <Link to="/howto">How To</Link>
        {showPricingTab && <Link to="/pricing">Pricing</Link>}
        <button className="logout-btn" onClick={handleLogoutClick}>
          Logout
        </button>
        <ThemeToggle isDarkMode={isDarkMode} onToggle={onToggle} />
      </>
    );
  } else if (isAnalysisPage) {
    navItems = (
      <>
        <Link to="/survey">My Feedback</Link>
        <Link to="/about">About</Link>
        {showPricingTab && <Link to="/pricing">Pricing</Link>}
        {token && (
          <button className="logout-btn" onClick={handleLogoutClick}>
            Logout
          </button>
        )}
        <ThemeToggle isDarkMode={isDarkMode} onToggle={onToggle} />
      </>
    );
  } else {
    if (token) {
      navItems = (
        <>
          <Link to="/">Home</Link>
          <Link to="/about">About</Link>
          <Link to="/results">Results</Link>
          <Link to="/try-it">Try It</Link>
          {showPricingTab && <Link to="/pricing">Pricing</Link>}
          <Link to="/survey">My Feedback</Link>
          <button className="logout-btn" onClick={handleLogoutClick}>
            Logout
          </button>
          <ThemeToggle isDarkMode={isDarkMode} onToggle={onToggle} />
        </>
      );
    } else {
      navItems = (
        <>
          <Link to="/">Home</Link>
          <Link to="/about">About</Link>
          <Link to="/results">Results</Link>
          <Link to="/try-it">Try It</Link>
          {showPricingTab && <Link to="/pricing">Pricing</Link>}
          <Link to="/register">Register</Link>
          <Link to="/login">Login</Link>
          <ThemeToggle isDarkMode={isDarkMode} onToggle={onToggle} />
        </>
      );
    }
  }

  return (
    <div className="banner">
      <div className="banner-left">
        <div className="logo-container">
          <Link to="/">
            <img
              src={isDarkMode ? "/logoLight.png" : "/logoDark.png"}
              alt="Anonysis Logo"
              className="my-logo"
            />
          </Link>
          <span className="trademark">™</span>
        </div>
      </div>

      <div className="banner-right" ref={menuRef}>
        <div className="navbar desktop-nav">{navItems}</div>

        <label htmlFor="menu-toggle" className="menu-icon">
          ☰
        </label>
        <input type="checkbox" id="menu-toggle" className="menu-toggle" />

        <div className="mobile-menu navbar">{navItems}</div>
      </div>
    </div>
  );
}
